import {
  EModule,
  EAccessLevel,
  IModuleConfig,
  EPrivilege,
} from './CreateRoleTile.types';

/**
 * Validation error messages
 */
export const ERROR_MESSAGES = {
  ROLE_NAME_REQUIRED: 'Role name is required',
  MODULE_REQUIRED: 'Please select at least one module',
  ROLE_MODULE_REQUIRED:
    'Please add role name and select at least one module to save the role.',
  ROLE_NAME_DUPLICATE: 'Role name already exists',
};

export const EModuleDefaultName: Record<EModule, string> = {
  [EModule.ASSESSMENTS]: 'assessment',
  [EModule.LIBRARY]: 'library',
  [EModule.SPEECH_SENSE]: 'speech_sense',
  [EModule.GENERAL]: 'general',
  [EModule.INTERVIEWS]: 'interview',
  [EModule.CONTESTS]: 'contest',
};

export const EPriviledgeDefaultName: Record<EPrivilege, string> = {
  [EPrivilege.MANAGE_ASSESSMENTS]: 'Manage Assessment',
  [EPrivilege.INVITATION]: 'Invitation',
  [EPrivilege.REPORTING]: 'Reporting',
  [EPrivilege.MANAGE_USER]: 'Manage User',
  [EPrivilege.DEFAULT]: 'Default',
  [EPrivilege.MANAGE_LIBRARY]: 'Manage Library',
  [EPrivilege.MANAGE_CONTEST]: 'Manage Contest',
  [EPrivilege.MANAGE_SETTINGS]: 'Contest Setting',
  [EPrivilege.MANAGE_PROBLEM]: 'Manage Problem',
};

// Reverse mapping
export const DefaultNameToEModule = Object.entries(EModuleDefaultName).reduce(
  (acc, [key, value]) => {
    acc[value] = key as EModule;
    return acc;
  },
  {} as Record<string, EModule>
);

/**
 * Available access levels
 */
export const ACCESS_LEVELS = [
  EAccessLevel.NO_VIEW,
  EAccessLevel.VIEW_ONLY,
  EAccessLevel.EDIT,
];

export const UNRESTRICTED_MODULE = [EModule.INTERVIEWS];

/**
 * Module configurations
 */
export const MODULE_CONFIGS: Record<EModule, IModuleConfig> = {
  [EModule.GENERAL]: {
    id: 'general-1',
    name: 'Company Management',
    category: 'General',
    access: ACCESS_LEVELS,
    defaultAccess: EAccessLevel.NO_VIEW,
    categoryData: [
      {
        name: 'Account Settings',
        privileges: [
          { id: 'accountSettings-1', name: 'Role Management' },
          {
            id: 'accountSettings-2',
            name: 'Invitation Manager ( Invite, resend, delete )',
          },
          {
            id: 'accountSettings-3',
            name: 'Activate / Deactivate Accounts',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.MANAGE_USER,
      },
    ],
  },
  [EModule.LIBRARY]: {
    id: 'library-1',
    name: 'Library Management',
    category: 'Library',
    access: ACCESS_LEVELS,
    defaultAccess: EAccessLevel.NO_VIEW,
    categoryData: [
      {
        name: 'Library Settings',
        privileges: [
          {
            id: 'libraryManagement-1',
            name: 'Question creation (Create, Publish)',
          },
          {
            id: 'libraryManagement-2',
            name: 'Question modification (Clone, Edit)',
          },
        ],
        accessLevel: ACCESS_LEVELS,
        defaultAccess: EAccessLevel.NO_VIEW,
        alias: EPrivilege.MANAGE_LIBRARY,
      },
    ],
  },
  [EModule.ASSESSMENTS]: {
    id: 'assessments-1',
    name: 'Assessment',
    category: 'Assessment',
    access: ACCESS_LEVELS,
    defaultAccess: EAccessLevel.NO_VIEW,
    categoryData: [
      {
        name: 'Assessment Administration',
        privileges: [
          {
            id: 'assessmentAdministration-1',
            name: 'Assessment creation (Edit questions & sections)',
          },
          {
            id: 'assessmentAdministration-2',
            name: 'Assessment management (Lock, clone, archive)',
          },
          {
            id: 'assessment Administration-3',
            name: 'Assessment settings (General, advanced, global)',
          },
          {
            id: 'assessmentAdministration-4',
            name: 'L&D Settings ( Publish to learn feed )',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.MANAGE_ASSESSMENTS,
      },
      {
        name: 'Invitation Management',
        privileges: [
          {
            id: 'invitationManagement-1',
            name: 'Candidate Invites (Single, bulk)',
          },
          {
            id: 'invitationManagement-2',
            name: 'Manage Invitations (Resend, delete, send reminders)',
          },
          {
            id: 'invitationManagement-3',
            name: 'Session Controls (Reset assessment, extend time)',
          },
          {
            id: 'invitationManagement-4',
            name: 'Access Sharing (Copy access URL)',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.INVITATION,
      },
      {
        name: 'Reporting Management',
        privileges: [
          {
            id: 'reportingManagement-1',
            name: 'Candidate Reports (Score questions, override proctoring verdict)',
          },
          {
            id: 'reportingManagement-2',
            name: 'Download Reports ( PDF, Excel )',
          },
        ],
        accessLevel: ACCESS_LEVELS,
        defaultAccess: EAccessLevel.NO_VIEW,
        alias: EPrivilege.REPORTING,
      },
    ],
  },
  [EModule.SPEECH_SENSE]: {
    id: 'speech-1',
    name: 'SpeechSense',
    category: 'SpeechSense',
    access: ACCESS_LEVELS,
    defaultAccess: EAccessLevel.EDIT,
    categoryData: [
      {
        name: 'SpeechSense Management',
        privileges: [
          {
            id: 'sassessmentAdministration-1',
            name: 'Speech Sense administration (Edit sections, lock, clone, archive)',
          },
          {
            id: 'sassessmentAdministration-2',
            name: 'Speech Sense settings (General, advanced)',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.MANAGE_ASSESSMENTS,
      },
      {
        name: 'Invitation Management',
        privileges: [
          {
            id: 'sinvitationManagement-1',
            name: 'Candidate Invites (Single, bulk)',
          },
          {
            id: 'sinvitationManagement-2',
            name: 'Manage Invitations (Resend, delete, send reminders)',
          },
          {
            id: 'sinvitationManagement-3',
            name: 'Session Controls (Reset assessment, extend time)',
          },
          {
            id: 'sinvitationManagement-4',
            name: 'Access Sharing (Copy access URL)',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.INVITATION,
      },
      {
        name: 'Reporting Management',
        privileges: [
          {
            id: 'sreportingManagement-1',
            name: 'Candidate Reports (Score questions, override proctoring verdict)',
          },
          {
            id: 'sreportingManagement-2',
            name: 'Download Reports ( PDF, Excel )',
          },
        ],
        accessLevel: ACCESS_LEVELS,
        defaultAccess: EAccessLevel.NO_VIEW,
        alias: EPrivilege.REPORTING,
      },
    ],
  },
  [EModule.CONTESTS]: {
    id: 'contests-1',
    name: 'Contests',
    category: 'Contests',
    access: ACCESS_LEVELS,
    defaultAccess: EAccessLevel.NO_VIEW,
    categoryData: [
      {
        name: 'Contest Management',
        privileges: [
          {
            id: 'contestAdministration-1',
            name: 'Contest creation (Create & Publish)',
          },
          {
            id: 'contestAdministration-2',
            name: 'Contest management (Draft & Archive)',
          },
          {
            id: 'contestAdministration-3',
            name: 'Landing Page Management',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.MANAGE_CONTEST,
      },
      {
        name: 'Manage Problems',
        privileges: [
          {
            id: 'contestProblemAdministration-1',
            name: 'Problem modification (Edit problems & sections)',
          },
          {
            id: 'contestProblemAdministration-2',
            name: 'Qualification Criteria Configuration',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.MANAGE_PROBLEM,
      },
      {
        name: 'Contest Setting',
        privileges: [
          {
            id: 'contestSettingManagement-1',
            name: 'Contest Details',
          },
          {
            id: 'contestSettingManagement-2',
            name: 'Participants Data Setting',
          },
          {
            id: 'contestSettingManagement-3',
            name: 'Proctoring miscllenous setting',
          },
          {
            id: 'contestSettingManagement-4',
            name: 'Support contacts',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.MANAGE_SETTINGS,
      },
      {
        name: 'Invitation Management',
        privileges: [
          {
            id: 'invitationManagement-1',
            name: 'Candidate Invites (Single, bulk)',
          },
          {
            id: 'invitationManagement-2',
            name: 'Manage Invitations (Resend, delete, send reminders)',
          },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.INVITATION,
      },
      {
        name: 'Reporting Management',
        privileges: [
          {
            id: 'reportingManagement-1',
            name: 'Candidate Reports (Score questions, override proctoring verdict)',
          },
          {
            id: 'reportingManagement-2',
            name: 'Download Reports ( PDF, Excel )',
          },
        ],
        accessLevel: ACCESS_LEVELS,
        defaultAccess: EAccessLevel.NO_VIEW,
        alias: EPrivilege.REPORTING,
      },
    ],
  },
  [EModule.INTERVIEWS]: {
    id: 'interviews-1',
    name: 'Interviews',
    category: 'Interviews',
    access: ACCESS_LEVELS,
    defaultAccess: EAccessLevel.EDIT,
    categoryData: [
      {
        name: 'Interview Management',
        privileges: [
          { id: 'interviews-1', name: 'Schedule Interviews' },
          { id: 'interviews-2', name: 'Conduct Interviews' },
        ],
        accessLevel: [EAccessLevel.VIEW_ONLY, EAccessLevel.EDIT],
        defaultAccess: EAccessLevel.VIEW_ONLY,
        alias: EPrivilege.DEFAULT,
      },
    ],
  },
};

/**
 * Default constants
 */
export const DEFAULT_VALUES = {
  ROLE_NAME_PLACEHOLDER: 'e.g., Admin, Moderator',
  INITIAL_MESSAGE:
    'Select a module to view its privileges.\nGet started to customize further!',
  SELECT_MODULE_MESSAGE: 'Please select the module to customize privileges.',
};
